/*----------------------------------
- PRODUCT FEATURE PAGES REGISTRY (SSOT)
----------------------------------*/

// Cross-runtime identity registry for the public /product/:slug feature
// presentation pages. Page copy lives in /client/catalogs/landing/product;
// server surfaces (sitemap, AI markdown mirrors) and client routes both
// key off this file.

/*----------------------------------
- TYPES
----------------------------------*/

export type ProductFeaturePageRegistryEntry = {
    slug: string;
    /** Menu label ("Search"). */
    label: string;
    pageTitle: string;
    metaDescription: string;
};

/*----------------------------------
- CONSTANTS
----------------------------------*/

export const PRODUCT_FEATURE_BASE_PATH = '/product';

export const productFeaturePagePath = (slug: string): string => `${PRODUCT_FEATURE_BASE_PATH}/${slug}`;

export const productFeaturePageMirrorPath = (slug: string): string => `/ai/pages/product-${slug}.md`;

/*----------------------------------
- CATALOG (SSOT)
----------------------------------*/

export const PRODUCT_FEATURE_PAGE_REGISTRY: readonly ProductFeaturePageRegistryEntry[] = [
    {
        slug: 'search',
        label: 'Search',
        pageTitle: 'Search One-Word Domains by Prompt, Keyword, or Filters',
        metaDescription:
            'Search a curated one-word domain database by prompt, keyword, or filters. Results carry price, status, renewal, history, and risk. Save any search as a Radar.',
    },
    {
        slug: 'verify',
        label: 'Verify',
        pageTitle: 'Verify a Domain Before You Buy: Price, Risk, History, and Proof',
        metaDescription:
            'Open the full case on any one-word domain: summary with factor bands, ask and renewal prices, resale estimate, trademark check, history, registrar paths.',
    },
    {
        slug: 'radars',
        label: 'Radars',
        pageTitle: 'Radars: Saved Domain Searches with Daily Fresh Matches',
        metaDescription:
            'Turn a useful search into a Radar. It keeps the prompt, filters, and logic, runs daily, and brings back fresh matches with in-app and email alerts.',
    },
    {
        slug: 'appraise',
        label: 'Appraise',
        pageTitle: 'Appraise Domains with Every Source at Once',
        metaDescription:
            'Value one name or a whole book in one run. Each name goes through every appraisal source you pick, on one shared axis, so you read a range, not one number.',
    },
    {
        slug: 'watchlists',
        label: 'Watchlists',
        pageTitle: 'Watchlists: A Live Shortlist That Tracks Drops and Deadlines',
        metaDescription:
            'Keep the names that matter in a live shortlist. Watchlists track price drops, expiring names, status changes, and risk flags, with notes, tags, and alerts.',
    },
] as const;

export const getProductFeaturePage = (slug: string): ProductFeaturePageRegistryEntry | null =>
    PRODUCT_FEATURE_PAGE_REGISTRY.find((entry) => entry.slug === slug) || null;
